/**
 * Template upload form component with full product information including Haravan integration
 */
import React, { useState } from 'react';
import { modalApi } from '@/services/modalApi';
import { Upload, Loader2, X, ExternalLink } from 'lucide-react';

interface TemplateUploadProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export default function TemplateUpload({ onSuccess, onCancel }: TemplateUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [material, setMaterial] = useState('');
  const [productType, setProductType] = useState<'phone_case' | 'coffee_cup'>('phone_case');
  const [isDefault, setIsDefault] = useState(false);

  // Coffee cup specific files
  const [cleanFile, setCleanFile] = useState<File | null>(null);
  const [maskFile, setMaskFile] = useState<File | null>(null);
  const [cleanPreview, setCleanPreview] = useState<string | null>(null);
  const [maskPreview, setMaskPreview] = useState<string | null>(null);

  // Haravan fields
  const [havaranProductId, setHavaranProductId] = useState('');
  const [havaranVariantId, setHavaranVariantId] = useState('');
  const [havaranProductHandle, setHavaranProductHandle] = useState('');

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type only
      if (!selectedFile.type.startsWith('image/')) {
        setError('Vui lòng chọn file hình ảnh');
        return;
      }

      setFile(selectedFile);
      setError(null);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleCleanFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        setError('Vui lòng chọn file hình ảnh');
        return;
      }

      setCleanFile(selectedFile);
      setError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        setCleanPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleMaskFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        setError('Vui lòng chọn file hình ảnh');
        return;
      }

      setMaskFile(selectedFile);
      setError(null);

      const reader = new FileReader();
      reader.onload = (e) => {
        setMaskPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Vui lòng chọn file');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Build metadata object
      const metadata: any = {
        name,
        category,
        material,
        product_type: productType,
        is_default: isDefault
      };

      // Add Haravan fields if provided
      if (havaranProductId.trim()) {
        metadata.haravan_product_id = havaranProductId.trim();
      }
      if (havaranVariantId.trim()) {
        metadata.haravan_variant_id = havaranVariantId.trim();
      }
      if (havaranProductHandle.trim()) {
        metadata.haravan_product_handle = havaranProductHandle.trim();
      }

      await modalApi.uploadTemplate(file, metadata, cleanFile, maskFile);

      // Reset form
      setFile(null);
      setCleanFile(null);
      setMaskFile(null);
      setName('');
      setCategory('');
      setMaterial('');
      setProductType('phone_case');
      setIsDefault(false);
      setHavaranProductId('');
      setHavaranVariantId('');
      setHavaranProductHandle('');
      setPreview(null);
      setCleanPreview(null);
      setMaskPreview(null);

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Tải lên thất bại');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Tải lên mẫu mới</h3>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hình ảnh mẫu (PNG/JPEG/GIF/WebP) *
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition">
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
              onChange={handleFileChange}
              className="hidden"
              id="template-file"
              disabled={uploading}
            />
            <label
              htmlFor="template-file"
              className="cursor-pointer flex flex-col items-center"
            >
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                {file ? file.name : 'Nhấp để tải lên hoặc kéo thả'}
              </span>
            </label>
          </div>
          {preview && (
            <div className="mt-4">
              <img
                src={preview}
                alt="Preview"
                className="max-w-full h-48 object-contain rounded-lg border border-gray-200"
              />
            </div>
          )}
        </div>

        {/* Coffee Cup Specific: Clean + Mask Images */}
        {productType === 'coffee_cup' && (
          <div className="border-t pt-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">
              🎯 Client-side Rendering (Coffee Cup Only)
            </h4>
            <p className="text-xs text-gray-600 mb-4">
              Upload 2 ảnh riêng biệt: Ảnh sạch (hiển thị) + Ảnh mask có khung đỏ (dò vị trí)
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Clean Image Upload */}
              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">
                  1. Clean Image (Sạch) *
                </label>
                <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 text-center hover:border-emerald-500 transition">
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleCleanFileChange}
                    className="hidden"
                    id="clean-file"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="clean-file"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-6 h-6 text-emerald-400 mb-1" />
                    <span className="text-xs text-gray-600">
                      {cleanFile ? cleanFile.name : 'Ảnh không có vết đỏ'}
                    </span>
                  </label>
                </div>
                {cleanPreview && (
                  <div className="mt-2">
                    <img
                      src={cleanPreview}
                      alt="Clean Preview"
                      className="w-full h-32 object-contain rounded-lg border border-emerald-200"
                    />
                  </div>
                )}
              </div>

              {/* Mask Image Upload */}
              <div>
                <label className="block text-sm font-medium text-red-700 mb-2">
                  2. Mask Image (Khung đỏ)
                </label>
                <div className="border-2 border-dashed border-red-300 rounded-lg p-4 text-center hover:border-red-500 transition">
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleMaskFileChange}
                    className="hidden"
                    id="mask-file"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="mask-file"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-6 h-6 text-red-400 mb-1" />
                    <span className="text-xs text-gray-600">
                      {maskFile ? maskFile.name : 'Ảnh có khung đỏ (optional)'}
                    </span>
                  </label>
                </div>
                {maskPreview && (
                  <div className="mt-2">
                    <img
                      src={maskPreview}
                      alt="Mask Preview"
                      className="w-full h-32 object-contain rounded-lg border border-red-200"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Basic Information Section */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Thông tin cơ bản</h4>

          {/* Product Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Loại sản phẩm *
            </label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value as 'phone_case' | 'coffee_cup')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            >
              <option value="phone_case">Ốp lưng điện thoại</option>
              <option value="coffee_cup">Cốc cà phê</option>
            </select>
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tên mẫu *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., iPhone 15 Pro Clear Case"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            />
          </div>

          {/* Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Danh mục *
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              placeholder="e.g., iPhone 15"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            />
          </div>

          {/* Material */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chất liệu *
            </label>
            <input
              type="text"
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              required
              placeholder="e.g., Clear, Matte Black"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            />
          </div>

          {/* Default checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-default"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              disabled={uploading}
            />
            <label htmlFor="is-default" className="text-sm text-gray-700">
              Đặt làm mẫu mặc định
            </label>
          </div>
        </div>

        {/* Haravan Integration Section */}
        <div className="border-t pt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
            Tích hợp Haravan (tùy chọn)
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </h4>
          <p className="text-xs text-gray-500 mb-4">
            Liên kết mẫu với sản phẩm trên Haravan để đồng bộ thông tin
          </p>

          {/* Haravan Product ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Haravan Product ID
            </label>
            <input
              type="text"
              value={havaranProductId}
              onChange={(e) => setHavaranProductId(e.target.value)}
              placeholder="e.g., 1071839642"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">ID sản phẩm trên Haravan</p>
          </div>

          {/* Haravan Variant ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Haravan Variant ID
            </label>
            <input
              type="text"
              value={havaranVariantId}
              onChange={(e) => setHavaranVariantId(e.target.value)}
              placeholder="e.g., 1234567890"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">ID biến thể sản phẩm (nếu có)</p>
          </div>

          {/* Haravan Product Handle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Haravan Product Handle
            </label>
            <input
              type="text"
              value={havaranProductHandle}
              onChange={(e) => setHavaranProductHandle(e.target.value)}
              placeholder="e.g., op-lung-iphone-17-pro-max"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">URL handle của sản phẩm trên Haravan</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            disabled={uploading}
          >
            Hủy
          </button>
          <button
            type="submit"
            disabled={uploading || !file}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Đang tải lên...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Tải lên mẫu
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
